import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, AddFrontendComponentInput, Environment, FrontendRepositoryInspection, MagentoTopologyView } from '../api';
import { valueSourceLabel } from '../labels';
import { Callout, Dialog, Field, IconButton, Input, Select, Subsection } from './primitives';
import { ErrorNotice } from './ui';
import { BuildSettingsFields, buildSettingsInput, defaultBuildSettings, nodeMajor, useFrontendFrameworks } from './BuildSettingsFields';
import type { BuildSettings } from './BuildSettingsFields';
import { RepositoryList } from './repository/RepositorySelector';
import { gitProviderNames, httpsCloneUrl } from './repository/useGitRepositories';
import type { RepositoryItem } from './repository/useGitRepositories';
import { useProjectRepositories } from './repository/useProjectRepositories';

interface AddFrontendModalProps {
  projectId: string;
  environment: Environment;
  onClose: () => void;
  onAdded: (applicationId: string) => void;
  /** Where the project's repository access is configured, for the notice
   * shown when there is none. */
  onConfigureAccess?: () => void;
}

interface VarRow {
  name: string;
  value: string;
  /** 'literal' when the operator types the value, otherwise a Commerce Ops
   * value resolved on every deployment (CO-52). */
  source: string;
  scope: string;
  visibility: string;
  required?: boolean;
  description?: string;
  /** What the repository analysis recommended, so a rejected mapping can be
   * taken back without retyping it. */
  recommended?: string;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/**
 * AddFrontendModal adds one more storefront to the project: its repository
 * (picked with the shared repository selector from what the project's
 * repository access can read, CO-73; typed only as an explicit fallback),
 * what Commerce Ops
 * found in it, the Magento website it serves, its hostnames and the
 * variables the code expects. Everything is one operation; nothing is
 * deployed until someone deploys it.
 */
export function AddFrontendModal({ projectId, environment, onClose, onAdded, onConfigureAccess }: AddFrontendModalProps) {
  const [name, setName] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  // Empty until a repository is picked: the provider's own default branch
  // is filled in then, and an empty value asks the API for it. Starting at
  // "main" submitted a branch nobody had chosen (CO-51, CO-71).
  const [branch, setBranch] = useState('');
  // The shared selector, fed by the project's own repository access.
  const repositorySource = useProjectRepositories(projectId);
  const [pickedRepo, setPickedRepo] = useState<RepositoryItem | null>(null);
  const [typingUrl, setTypingUrl] = useState(false);
  const [inspection, setInspection] = useState<FrontendRepositoryInspection | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectError, setInspectError] = useState<Error | null>(null);
  // The shared build settings (CO-90). The framework stays empty until the
  // repository analysis names one or the person picks: building a Nuxt
  // storefront as Next.js is worse than asking.
  const [build, setBuild] = useState<BuildSettings>(defaultBuildSettings);
  const [healthPath, setHealthPath] = useState('/');
  const frameworks = useFrontendFrameworks();
  const [topology, setTopology] = useState<MagentoTopologyView | null>(null);
  const [websiteId, setWebsiteId] = useState('');
  const [storeViewId, setStoreViewId] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [aliases, setAliases] = useState('');
  const [tlsMode, setTlsMode] = useState('acme');
  const [vars, setVars] = useState<VarRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // The body scrolls; a refused submit is brought into view.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error]);

  useEffect(() => {
    let cancelled = false;
    api.getMagentoTopology(environment.id)
      .then((r) => {
        if (cancelled) return;
        setTopology(r.topology);
        const active = r.topology.websites.filter((w) => w.website.status === 'active');
        const only = active[0];
        if (active.length === 1 && only) setWebsiteId(only.website.id);
      })
      .catch(() => { /* no topology yet */ });
    return () => { cancelled = true; };
  }, [environment.id]);

  const accessConfigured = repositorySource.source !== null && repositorySource.source !== 'none';
  // Typing a URL is the fallback, not the default: with repository access
  // the repository is picked from what that access can actually read.
  const manualEntry = typingUrl || repositorySource.source === 'none';
  const websites = useMemo(() => (topology?.websites ?? []).filter((w) => w.website.status === 'active'), [topology]);
  const storeViews = useMemo(() => websites.find((w) => w.website.id === websiteId)?.store_views.filter((v) => v.is_active) ?? [], [websites, websiteId]);
  const takenWebsites = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of topology?.websites ?? []) {
      for (const fe of w.frontends) m.set(w.website.id, fe.application?.name ?? 'another frontend');
    }
    return m;
  }, [topology]);

  function pickRepository(repository: RepositoryItem) {
    const url = httpsCloneUrl(repositorySource.provider, repository.fullName);
    const chosenBranch = repository.defaultBranch || repository.branches[0] || '';
    setPickedRepo(repository);
    setRepoUrl(url);
    setBranch(chosenBranch);
    setInspection(null);
    if (!name) setName(repository.fullName.split('/')[1]?.replace(/[-_]/g, ' ') ?? '');
    // Reading the repository is what fills the build settings; do it
    // straight away rather than making the person find the button.
    void inspect(url, chosenBranch);
  }

  async function inspect(url = repoUrl, onBranch = branch) {
    if (!url.trim()) {
      setInspectError(new Error('Pick the frontend repository first.'));
      return;
    }
    setInspecting(true);
    setInspectError(null);
    try {
      // An empty branch asks the provider for the repository's own default.
      const res = await api.inspectFrontendRepository(projectId, url.trim(), onBranch.trim());
      setInspection(res);
      const a = res.analysis;
      if (!res.fallback) {
        // The analysis reports script commands; the component stores the
        // package.json script names Commerce Ops runs.
        const scripts = a.available_scripts ?? {};
        setBuild((current) => ({
          framework: a.framework || '',
          packageManager: a.package_manager || 'npm',
          nodeVersion: nodeMajor(a.node_version) || defaultBuildSettings.nodeVersion,
          buildScript: scripts.build !== undefined ? 'build' : current.buildScript,
          startScript: scripts.start !== undefined ? 'start' : scripts.serve !== undefined ? 'serve' : current.startScript,
          workingDirectory: a.working_directory || '/',
          outputMode: a.output_mode === 'static' ? 'static_files' : 'node_server',
        }));
        // The repository owns the names; Commerce Ops only says which of
        // its own values it can fill each one with. Nothing is invented:
        // an example in the repository stays an example.
        setVars(
          (a.detected_env_vars ?? []).map((v) => ({
            name: v.name,
            value: '',
            source: v.source ?? 'literal',
            recommended: v.source,
            scope: v.default_scope ?? 'build_and_runtime',
            visibility: v.default_visibility ?? 'public',
            required: v.required,
            description: v.description ?? (v.example_value ? `Example in the repository: ${v.example_value}` : undefined),
          })),
        );
      }
    } catch (err) {
      setInspectError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setInspecting(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(new Error('Give the frontend a name — usually the shop it serves, for example "Outdoor storefront".'));
      return;
    }
    if (!repoUrl.trim()) {
      setError(new Error(manualEntry ? 'Enter the repository the frontend is deployed from.' : 'Pick the repository the frontend is deployed from.'));
      return;
    }
    if (!build.framework) {
      setError(new Error('Choose the framework. Commerce Ops could not name it from the repository, and it decides how the storefront is installed, built and started — use “Other Node.js app” if none of the listed ones fit.'));
      return;
    }
    if (websites.length > 1 && !websiteId) {
      setError(new Error('This installation has several websites; choose the one this frontend serves.'));
      return;
    }
    setSaving(true);
    const input: AddFrontendComponentInput = {
      name: name.trim(),
      slug: slugify(name),
      repository_url: repoUrl.trim(),
      default_branch: branch.trim(),
      ...buildSettingsInput(build),
      health_check: healthPath.trim() && healthPath.trim() !== '/' ? { path: healthPath.trim(), expected_status: 200 } : null,
      environment_id: environment.id,
      website_id: websiteId || undefined,
      store_view_id: storeViewId || undefined,
      primary_domain: primaryDomain.trim().toLowerCase() || undefined,
      alias_domains: aliases.split(/[\s,]+/).map((a) => a.trim().toLowerCase()).filter(Boolean),
      tls_mode: tlsMode,
      // A variable that takes a Commerce Ops value carries no value of its
      // own, so it is saved on its name and source alone.
      env_vars: vars
        .filter((v) => v.name.trim() && (v.source !== 'literal' || v.value.trim()))
        .map((v) => ({
          name: v.name.trim(),
          value: v.source === 'literal' ? v.value : '',
          source: v.source,
          scope: v.scope,
          visibility: v.source === 'literal' ? v.visibility : 'public',
          required: v.required ?? false,
        })),
    };
    try {
      const res = await api.addFrontendComponent(projectId, input);
      onAdded(res.application.id);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      size="lg"
      title="Add a frontend"
      description="Nothing is deployed by adding it."
      onClose={onClose}
      onSubmit={submit}
      busy={saving}
      testId="add-frontend-modal"
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={saving} data-testid="add-frontend-submit">
            {saving ? 'Adding' : 'Add frontend'}
          </button>
        </>
      }
    >
      <div ref={errorRef}>
        <ErrorNotice error={error} />
      </div>

      <div className="build-form">
        <Subsection title="Repository">
          {manualEntry ? (
            <div className="build-form__stack" data-testid="add-frontend-manual">
              {repositorySource.source === 'none' ? (
                <Callout
                  tone="warning"
                  title="This project has no repository access yet."
                  testId="add-frontend-no-access"
                  actions={
                    onConfigureAccess ? (
                      <button type="button" className="button button--secondary button--small" onClick={onConfigureAccess}>
                        Configure repository access
                      </button>
                    ) : null
                  }
                />
              ) : null}
              <Field
                label="Repository clone URL"
                hint="A public repository, or one this project’s repository access can read. Use the HTTPS address."
              >
                <Input
                  type="text"
                  className="input--mono"
                  value={repoUrl}
                  onChange={(e) => { setRepoUrl(e.target.value); setPickedRepo(null); setInspection(null); }}
                  placeholder="https://github.com/your-org/your-storefront.git"
                  spellCheck={false}
                  autoComplete="off"
                  data-testid="add-frontend-repo"
                />
              </Field>
              {accessConfigured ? (
                <button type="button" className="link-button build-form__switch" onClick={() => setTypingUrl(false)}>
                  Pick from {gitProviderNames[repositorySource.provider]} instead
                </button>
              ) : null}
            </div>
          ) : (
            <div className="build-form__stack">
              <span className="field__label" id="addfe-repo-label">
                Frontend repository
                {repositorySource.source === 'github_app'
                  ? ' from the organization’s GitHub App'
                  : repositorySource.account
                    ? ` from ${gitProviderNames[repositorySource.provider]} (${repositorySource.account})`
                    : ''}
              </span>
              <RepositoryList
                git={repositorySource}
                selected={pickedRepo?.fullName ?? ''}
                onSelect={pickRepository}
                label="Frontend repository"
                filterLabel="Filter repositories"
                compact
                testId="add-frontend-repo-list"
              />
              <button type="button" className="link-button build-form__switch" onClick={() => { setTypingUrl(true); setPickedRepo(null); }} data-testid="add-frontend-type-url">
                Enter a clone URL instead
              </button>
            </div>
          )}
          <div className="build-form__row">
            <Field label="Name" hint="Shown everywhere this component appears: deployments, alerts, logs.">
              <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Outdoor storefront" required data-testid="add-frontend-name" />
            </Field>
            <Field label="Branch to deploy">
              {pickedRepo && pickedRepo.branches.length > 0 ? (
                <Select value={branch} onChange={(e) => { setBranch(e.target.value); setInspection(null); }} data-testid="add-frontend-branch">
                  {pickedRepo.branches.map((b) => (
                    <option key={b} value={b}>{b}{b === pickedRepo.defaultBranch ? ' (default)' : ''}</option>
                  ))}
                </Select>
              ) : (
                <Input
                  type="text"
                  className="input--mono"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  placeholder={repoUrl ? 'Repository’s default branch' : 'Pick a repository first'}
                  disabled={!repoUrl}
                  spellCheck={false}
                  data-testid="add-frontend-branch"
                />
              )}
            </Field>
          </div>
          <div className="build-form__inspect">
            <button type="button" className="button button--secondary button--small" onClick={() => inspect()} disabled={inspecting || !repoUrl.trim()} title={repoUrl.trim() ? undefined : 'Pick or enter the repository first.'} data-testid="add-frontend-inspect">
              {inspecting ? 'Reading the repository' : inspection ? 'Read the repository again' : 'Read the repository'}
            </button>
          </div>
          <ErrorNotice error={inspectError} />
          {inspection?.fallback ? (
            <Callout tone="warning" title="Could not read the repository." testId="add-frontend-fallback">
              {inspection.fallback.message}
            </Callout>
          ) : inspection ? (
            <Callout tone="success" title={`Read ${inspection.provenance.repository ?? 'the repository'}`} testId="add-frontend-inspected">
              At <code>{inspection.provenance.commit_sha?.slice(0, 7)}</code> on <code>{inspection.provenance.branch}</code>:{' '}
              {inspection.analysis.framework} with {inspection.analysis.package_manager}, Node {inspection.analysis.node_version}
              {inspection.analysis.detected_env_vars?.length ? `, ${inspection.analysis.detected_env_vars.length} variables expected` : ''}.
              {inspection.analysis.deployment_supported === false ? ' Commerce Ops cannot deploy this framework yet; the settings can still be saved.' : ''}
            </Callout>
          ) : null}
        </Subsection>

        <Subsection title="Build and start">
          <BuildSettingsFields value={build} onChange={setBuild} frameworks={frameworks} testIdPrefix="add-frontend" requireFramework disabled={saving} />
          <div className="build-form__row build-form__row--3">
            <Field
              label="Health check path"
              hint="Checked on every candidate before traffic switches, and after Magento deploys."
            >
              <Input type="text" className="input--mono" value={healthPath} onChange={(e) => setHealthPath(e.target.value)} placeholder="/" spellCheck={false} data-testid="add-frontend-health" />
            </Field>
          </div>
        </Subsection>

        <Subsection
          title={`Store and hostnames in ${environment.name}`}
          description="DNS is never changed by Commerce Ops: point the hostnames at the server yourself."
        >
          {websites.length === 0 ? (
            <p className="build-form__note">
              No Magento website is known for this environment yet. The frontend is added without a website binding; bind it once the topology has been discovered.
            </p>
          ) : (
            <div className="build-form__row">
              <Field
                label="Magento website this storefront serves"
                hint="Every GraphQL call this frontend makes carries this website’s store code; its checks fail if the backend answers with another store."
              >
                <Select value={websiteId} onChange={(e) => { setWebsiteId(e.target.value); setStoreViewId(''); }} data-testid="add-frontend-website">
                  <option value="">Choose a website</option>
                  {websites.map((w) => (
                    <option key={w.website.id} value={w.website.id}>
                      {w.website.name} ({w.website.code}){takenWebsites.has(w.website.id) ? `, already served by ${takenWebsites.get(w.website.id)}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Store view">
                <Select value={storeViewId} onChange={(e) => setStoreViewId(e.target.value)} disabled={!websiteId} title={websiteId ? undefined : 'Choose the website first.'}>
                  <option value="">Default store view</option>
                  {storeViews.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
          <div className="build-form__row build-form__row--3">
            <Field label="Primary hostname" hint="The canonical address; other hostnames redirect here.">
              <Input type="text" className="input--mono" value={primaryDomain} onChange={(e) => setPrimaryDomain(e.target.value)} placeholder="shop.example.com" spellCheck={false} data-testid="add-frontend-domain" />
            </Field>
            <Field label="Aliases" hint="Comma or space separated.">
              <Input type="text" className="input--mono" value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="www.shop.example.com" spellCheck={false} />
            </Field>
            <Field label="HTTPS">
              <Select value={tlsMode} onChange={(e) => setTlsMode(e.target.value)}>
                <option value="acme">Let’s Encrypt certificate</option>
                <option value="none">HTTP only</option>
              </Select>
            </Field>
          </div>
        </Subsection>

        <Subsection
          title={`Variables in ${environment.name}`}
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => setVars((v) => [...v, { name: '', value: '', source: 'literal', scope: 'build_and_runtime', visibility: 'public' }])}>
              Add variable
            </button>
          }
        >
          {vars.length === 0 ? (
            <p className="build-form__note">None yet</p>
          ) : (
            <ul className="addfe__vars">
              {vars.map((v, i) => (
                <li key={i} className="addfe__var" data-testid={`addfe-var-${v.name || i}`}>
                  <Input type="text" value={v.name} onChange={(e) => setVars((all) => all.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="MAGENTO_STORE_VIEW" aria-label="Variable name" spellCheck={false} translate="no" />
                  {v.source === 'literal' ? (
                    <Input type={v.visibility === 'secret' ? 'password' : 'text'} value={v.value} onChange={(e) => setVars((all) => all.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} placeholder={v.required ? 'required' : 'value'} aria-label={`Value of ${v.name || 'variable'}`} />
                  ) : (
                    <span className="addfe__var-source">{valueSourceLabel(v.source)}</span>
                  )}
                  <Select value={v.scope} onChange={(e) => setVars((all) => all.map((x, j) => (j === i ? { ...x, scope: e.target.value } : x)))} aria-label="When it is needed">
                    <option value="build_and_runtime">Build and runtime</option>
                    <option value="build">Build only</option>
                    <option value="runtime">Runtime only</option>
                  </Select>
                  {v.source === 'literal' ? (
                    <Select value={v.visibility} onChange={(e) => setVars((all) => all.map((x, j) => (j === i ? { ...x, visibility: e.target.value } : x)))} aria-label="Visibility">
                      <option value="public">Public</option>
                      <option value="secret">Secret</option>
                    </Select>
                  ) : (
                    <span className="muted addfe__var-fixed" title="A Commerce Ops value is configuration, not a credential.">Public</span>
                  )}
                  <IconButton icon="close" size="sm" variant="ghost" label={`Remove ${v.name || 'variable'}`} onClick={() => setVars((all) => all.filter((_, j) => j !== i))} />
                  {v.source !== 'literal' || v.recommended || v.description ? (
                  <span className="muted addfe__var-hint">
                    {v.source === 'literal' ? (
                      <>
                        {v.recommended ? (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => setVars((all) => all.map((x, j) => (j === i ? { ...x, source: x.recommended ?? 'literal', value: '', visibility: 'public' } : x)))}
                          >
                            Fill from {valueSourceLabel(v.recommended)}
                          </button>
                        ) : null}
                        {v.recommended && v.description ? ' · ' : ''}
                        {v.description ?? ''}
                      </>
                    ) : (
                      <>
                        Commerce Ops fills this on every deployment.{' '}
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setVars((all) => all.map((x, j) => (j === i ? { ...x, source: 'literal' } : x)))}
                        >
                          Enter a value instead
                        </button>
                      </>
                    )}
                  </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Subsection>
      </div>
    </Dialog>
  );
}
